import { Ionicons } from '@expo/vector-icons';
import type { AuditLogRow } from './auditLogs';
import { getStockMovementReasonLabel } from '../../shared/domain/stockMovementReasons';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { formatMoney } from '../../shared/format/public-api';

export { formatAuditEventLabel } from './auditLogs';

export type AuditDetailRow = { label: string; value: string };

/** Timestamp de evento de auditoría para listas y detalle. */
export function formatAuditWhen(iso: string | null | undefined): string {
  return formatLocalDateTime(iso);
}

/** Últimos caracteres de un UUID para referencia breve (lista). */
export function shortId(id: string, tail = 8): string {
  const s = String(id).replace(/-/g, '');
  if (s.length <= tail) return id;
  return `…${s.slice(-tail)}`;
}

function yn(v: unknown): string {
  return v === true ? 'sí' : v === false ? 'no' : String(v ?? '—');
}

const MODULE_LABELS: Record<string, string> = {
  BranchSettings: 'Configuración de sucursal',
  Pos: 'Punto de venta',
  Reports: 'Informes',
  Inventory: 'Stock',
  Catalog: 'Catálogo',
  Customers: 'Clientes',
  Promotions: 'Promociones',
  Admin: 'Administración',
};

export function formatModuleLabel(module: string): string {
  return MODULE_LABELS[module] ?? module;
}

/** Resumen en una línea para lista (permisos canView/canEdit u objeto similar). */
export function formatPermissionDiffShort(before: unknown, after: unknown): string | null {
  const b = before && typeof before === 'object' ? (before as Record<string, unknown>) : {};
  const a = after && typeof after === 'object' ? (after as Record<string, unknown>) : {};
  const keys = new Set([...Object.keys(b), ...Object.keys(a)]);
  const parts: string[] = [];
  for (const k of keys) {
    const label =
      k === 'canView'
        ? 'Ver'
        : k === 'canEdit'
          ? 'Editar'
          : k === 'canVoid'
            ? 'Anular ventas'
            : k === 'canManage'
              ? 'Gestionar'
              : k;
    const vb = b[k];
    const va = a[k];
    if (vb === va) continue;
    parts.push(`${label}: ${yn(vb)} → ${yn(va)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatCatalogProductUpdateSubtitle(meta: Record<string, unknown>): string | null {
  const before = meta.before as Record<string, unknown> | undefined;
  const after = meta.after as Record<string, unknown> | undefined;
  if (!before || !after) return null;
  const parts: string[] = [];
  if (before.name !== after.name) {
    parts.push(`Nombre: "${String(before.name)}" → "${String(after.name)}"`);
  }
  if (before.price !== after.price) {
    parts.push(`Precio: ${String(before.price)} → ${String(after.price)}`);
  }
  if (before.sku !== after.sku) {
    parts.push(`SKU: ${String(before.sku)} → ${String(after.sku)}`);
  }
  if (before.categoryId !== after.categoryId) {
    parts.push(`Categoría: ${String(before.categoryId)} → ${String(after.categoryId)}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Sin cambios detectados';
}


function branchRef(meta: Record<string, unknown>): string | null {
  const id =
    (typeof meta.branch_id === 'string' && meta.branch_id) ||
    (typeof meta.branchId === 'string' && meta.branchId) ||
    null;
  return id ? `Sucursal ${shortId(id)}` : null;
}

/**
 * Subtítulo corto para la lista (sin UUIDs largos; prioriza texto humano).
 */
export function getAuditListSubtitle(row: AuditLogRow): string | null {
  const meta = row.metadata ?? {};
  const t = row.event_type;

  if (t === 'employee.permission.change') {
    const mod = typeof meta.module === 'string' ? formatModuleLabel(meta.module) : 'Permisos';
    const diff = formatPermissionDiffShort(meta.before, meta.after);
    return diff ? `${mod} · ${diff}` : mod;
  }

  if (t === 'employee.revoked' || t === 'employee.branch.add' || t === 'employee.branch.remove') {
    const name = typeof meta.employeeName === 'string' && meta.employeeName.trim() ? meta.employeeName.trim() : null;
    const br = branchRef(meta);
    const parts = [name, br].filter(Boolean) as string[];
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  if (t.startsWith('invitation.') || t === 'register.success') {
    if (typeof meta.email === 'string' && meta.email) {
      const role =
        (typeof meta.role === 'string' && meta.role) ||
        (typeof meta.role_assigned === 'string' && meta.role_assigned) ||
        '';
      return role ? `${meta.email} · ${role}` : meta.email;
    }
  }

  if (t === 'login.success' && typeof meta.email === 'string' && meta.email) {
    return meta.email;
  }

  if (t === 'pos.sale.create' || t === 'pos.sale.void') {
    const parts: string[] = [];
    if (typeof meta.total === 'number') parts.push(`Total ${formatMoney(meta.total)}`);
    if (typeof meta.itemCount === 'number') parts.push(`${meta.itemCount} ítems`);
    if (typeof meta.paymentStatus === 'string' && meta.paymentStatus) parts.push(meta.paymentStatus);
    if (t === 'pos.sale.void' && typeof meta.voidReason === 'string' && meta.voidReason.trim()) {
      parts.push(meta.voidReason.trim());
    }
    return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
  }

  if (t === 'customer.charge' || t === 'customer.payment') {
    const parts: string[] = [];
    if (typeof meta.amount === 'number') parts.push(`${formatMoney(Math.abs(meta.amount))}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  if (t === 'stock.adjust') {
    const parts: string[] = [];
    if (typeof meta.delta === 'number') parts.push(`Δ ${meta.delta > 0 ? '+' : ''}${meta.delta}`);
    if (typeof meta.reason === 'string' && meta.reason) {
      parts.push(getStockMovementReasonLabel(meta.reason));
    }
    return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
  }

  if (t === 'stock.bulk_coefficient' && typeof meta.coefficient === 'number') {
    return `Coef. ${meta.coefficient}%`;
  }

  if (t.startsWith('stock.transfer.')) {
    const parts: string[] = [];
    if (typeof meta.quantity === 'number') parts.push(`${meta.quantity} u.`);
    if (typeof meta.fromBranchId === 'string' && typeof meta.toBranchId === 'string') {
      parts.push(`${shortId(meta.fromBranchId)} → ${shortId(meta.toBranchId)}`);
    }
    return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
  }

  if (t === 'stock.receive') {
    const parts: string[] = [];
    if (typeof meta.quantity === 'number') parts.push(`+${meta.quantity}`);
    else if (typeof meta.delta === 'number') parts.push(`+${meta.delta}`);
    return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
  }

  if (t === 'catalog.product.update') {
    return formatCatalogProductUpdateSubtitle(meta);
  }

  if (t === 'catalog.product.deactivate' || t === 'catalog.product.reactivate') {
    if (typeof meta.productId === 'string') return `Producto ${shortId(meta.productId)}`;
    return null;
  }

  if (t.startsWith('catalog.category.')) {
    if (typeof meta.slug === 'string' && meta.slug) return meta.slug;
    if (typeof meta.categoryId === 'string') return `Categoría ${shortId(meta.categoryId)}`;
    return null;
  }

  if (t.startsWith('pos.')) {
    if (t === 'pos.external_service.create') {
      const parts: string[] = [];
      if (typeof meta.type === 'string') parts.push(meta.type);
      if (typeof meta.amount === 'number') parts.push(formatMoney(meta.amount));
      if (typeof meta.paymentMethod === 'string') parts.push(meta.paymentMethod);
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
  }

  if (t.startsWith('reports.')) {
    if (t === 'reports.external_service.create') {
      const parts: string[] = [];
      if (typeof meta.type === 'string') parts.push(meta.type);
      if (typeof meta.externalStoreId === 'string') parts.push(`Tienda ${shortId(meta.externalStoreId)}`);
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
    if (t === 'reports.cash.open') {
      const parts: string[] = [];
      if (typeof meta.openingAmount === 'number') parts.push(`Fondo ${formatMoney(meta.openingAmount)}`);
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
    if (t === 'reports.cash.close') {
      const parts: string[] = [];
      if (typeof meta.difference === 'number') parts.push(`Dif. ${formatMoney(meta.difference)}`);
      if (typeof meta.closingAmount === 'number') parts.push(`Contado ${formatMoney(meta.closingAmount)}`);
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
    if (t === 'reports.cash.outflow.create' || t === 'reports.cash.outflow.void') {
      const parts: string[] = [];
      if (typeof meta.amount === 'number') parts.push(formatMoney(meta.amount));
      if (typeof meta.category === 'string' && meta.category) parts.push(meta.category);
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
    if (t === 'expense.create' || t === 'expense.void' || t === 'expense.settlement' || t === 'expense.settlement.void') {
      const parts: string[] = [];
      if (typeof meta.amount === 'number') parts.push(formatMoney(meta.amount));
      if (typeof meta.categorySlug === 'string') parts.push(meta.categorySlug);
      if (typeof meta.method === 'string') parts.push(meta.method);
      if (typeof meta.paidAt === 'string' && meta.paidAt) {
        parts.push(meta.paidAt.slice(0, 10));
      }
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
    return branchRef(meta);
  }

  if (t.startsWith('branch.settings.')) {
    if (t === 'branch.settings.manual_discount_threshold') {
      const parts: string[] = [];
      if (typeof meta.manualDiscountMaxPercent === 'number') {
        parts.push(`Tope ${meta.manualDiscountMaxPercent}%`);
      }
      if (typeof meta.manualDiscountMaxAmount === 'number') {
        parts.push(`${formatMoney(meta.manualDiscountMaxAmount)}`);
      }
      return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
    }
    return branchRef(meta);
  }

  if (t.startsWith('promotions.promotion.')) {
    const parts: string[] = [];
    if (typeof meta.name === 'string' && meta.name.trim()) parts.push(meta.name.trim());
    if (typeof meta.kind === 'string' && meta.kind) parts.push(meta.kind);
    return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
  }

  if (t === 'pos.promotion.apply_manual') {
    const parts: string[] = [];
    if (typeof meta.discountAmount === 'number') parts.push(`-${formatMoney(meta.discountAmount)}`);
    if (typeof meta.reason === 'string' && meta.reason.trim()) parts.push(meta.reason.trim());
    return parts.length > 0 ? parts.join(' · ') : branchRef(meta);
  }

  // Fallback: montos / totales / cantidades sin listar todos los IDs
  const fallback: string[] = [];
  if (typeof meta.amount === 'number') fallback.push(`Monto ${formatMoney(meta.amount)}`);
  if (typeof meta.total === 'number') fallback.push(`Total ${formatMoney(meta.total)}`);
  if (typeof meta.itemCount === 'number') fallback.push(`${meta.itemCount} ítems`);
  if (typeof meta.affectedRows === 'number') fallback.push(`${meta.affectedRows} filas`);
  if (fallback.length > 0) return fallback.join(' · ');

  return branchRef(meta);
}

function pushMetaValue(
  out: AuditDetailRow[],
  used: Set<string>,
  key: string,
  label: string,
  value: unknown
) {
  if (value === undefined) return;
  used.add(key);
  out.push({ label, value: formatDetailValue(value) });
}

function formatDetailValue(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

/**
 * Filas etiquetadas para el modal (incluye UUIDs completos y JSON formateado).
 */
export function getAuditDetailRows(row: AuditLogRow): AuditDetailRow[] {
  const meta = row.metadata ?? {};
  const t = row.event_type;
  const used = new Set<string>();
  const out: AuditDetailRow[] = [];

  if (t === 'employee.permission.change') {
    if (typeof meta.employeeName === 'string' && meta.employeeName) {
      pushMetaValue(out, used, 'employeeName', 'Nombre', meta.employeeName);
    }
    if (typeof meta.employeeId === 'string') pushMetaValue(out, used, 'employeeId', 'ID empleado', meta.employeeId);
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
    if (typeof meta.module === 'string') pushMetaValue(out, used, 'module', 'Módulo', formatModuleLabel(meta.module));
    if (typeof meta.field === 'string') pushMetaValue(out, used, 'field', 'Campo tocado', meta.field);
    const diff = formatPermissionDiffShort(meta.before, meta.after);
    if (diff) {
      out.push({ label: 'Cambio (resumen)', value: diff });
    }
    pushMetaValue(out, used, 'before', 'Antes (JSON)', meta.before);
    pushMetaValue(out, used, 'after', 'Después (JSON)', meta.after);
  } else if (t.startsWith('invitation.') || t === 'login.success' || t === 'register.success') {
    if (typeof meta.email === 'string') pushMetaValue(out, used, 'email', 'Correo', meta.email);
    if (typeof meta.role === 'string') pushMetaValue(out, used, 'role', 'Rol', meta.role);
    if (typeof meta.role_assigned === 'string') {
      pushMetaValue(out, used, 'role_assigned', 'Rol asignado', meta.role_assigned);
    }
    if (Array.isArray(meta.branch_ids) && meta.branch_ids.length > 0) {
      pushMetaValue(out, used, 'branch_ids', 'Sucursales', (meta.branch_ids as string[]).join(', '));
    }
    if (typeof meta.invitation_id === 'string') pushMetaValue(out, used, 'invitation_id', 'Invitación', meta.invitation_id);
    if (typeof meta.status === 'string') pushMetaValue(out, used, 'status', 'Estado', meta.status);
  } else if (t === 'catalog.product.update') {
    if (typeof meta.productId === 'string') pushMetaValue(out, used, 'productId', 'Producto', meta.productId);
    pushMetaValue(out, used, 'before', 'Antes', meta.before);
    pushMetaValue(out, used, 'after', 'Después', meta.after);
  } else if (t === 'pos.sale.create' || t === 'pos.sale.void') {
    if (typeof meta.total === 'number') pushMetaValue(out, used, 'total', 'Total', formatMoney(meta.total));
    if (typeof meta.itemCount === 'number') pushMetaValue(out, used, 'itemCount', 'Ítems', meta.itemCount);
    if (typeof meta.fiscalMode === 'string') pushMetaValue(out, used, 'fiscalMode', 'Modo fiscal', meta.fiscalMode);
    if (typeof meta.paymentStatus === 'string') pushMetaValue(out, used, 'paymentStatus', 'Pago', meta.paymentStatus);
    if (typeof meta.saleId === 'string') pushMetaValue(out, used, 'saleId', 'Venta', meta.saleId);
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
    if (typeof meta.voidReason === 'string') pushMetaValue(out, used, 'voidReason', 'Motivo de anulación', meta.voidReason);
  } else if (t.startsWith('stock.')) {
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
    if (typeof meta.fromBranchId === 'string') pushMetaValue(out, used, 'fromBranchId', 'Origen', meta.fromBranchId);
    if (typeof meta.toBranchId === 'string') pushMetaValue(out, used, 'toBranchId', 'Destino', meta.toBranchId);
    if (typeof meta.productId === 'string') pushMetaValue(out, used, 'productId', 'Producto', meta.productId);
    if (typeof meta.stockId === 'string') pushMetaValue(out, used, 'stockId', 'Stock', meta.stockId);
    if (typeof meta.movementId === 'string') pushMetaValue(out, used, 'movementId', 'Movimiento', meta.movementId);
    if (typeof meta.transferId === 'string') pushMetaValue(out, used, 'transferId', 'Transferencia', meta.transferId);
    if (typeof meta.quantity === 'number') pushMetaValue(out, used, 'quantity', 'Cantidad', meta.quantity);
    if (typeof meta.delta === 'number') pushMetaValue(out, used, 'delta', 'Delta', meta.delta);
    if (typeof meta.reason === 'string') {
      pushMetaValue(out, used, 'reason', 'Motivo', getStockMovementReasonLabel(meta.reason));
    }
    if (typeof meta.coefficient === 'number') pushMetaValue(out, used, 'coefficient', 'Coeficiente %', meta.coefficient);
    if (typeof meta.affectedRows === 'number') pushMetaValue(out, used, 'affectedRows', 'Filas afectadas', meta.affectedRows);
  } else if (t.startsWith('customer.')) {
    if (typeof meta.customerId === 'string') pushMetaValue(out, used, 'customerId', 'Cliente', meta.customerId);
    if (typeof meta.amount === 'number') pushMetaValue(out, used, 'amount', 'Monto', formatMoney(meta.amount));
    if (typeof meta.movementId === 'string') pushMetaValue(out, used, 'movementId', 'Movimiento', meta.movementId);
    if (typeof meta.saleId === 'string') pushMetaValue(out, used, 'saleId', 'Venta', meta.saleId);
  } else if (t.startsWith('expense.')) {
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
    if (typeof meta.expenseId === 'string') pushMetaValue(out, used, 'expenseId', 'Gasto', meta.expenseId);
    if (typeof meta.settlementId === 'string') pushMetaValue(out, used, 'settlementId', 'Liquidación', meta.settlementId);
    if (typeof meta.method === 'string') pushMetaValue(out, used, 'method', 'Método', meta.method);
    if (typeof meta.amount === 'number') pushMetaValue(out, used, 'amount', 'Monto', formatMoney(meta.amount));
    if (typeof meta.paidAt === 'string') pushMetaValue(out, used, 'paidAt', 'Fecha de pago', meta.paidAt);
    if (typeof meta.categorySlug === 'string') pushMetaValue(out, used, 'categorySlug', 'Categoría', meta.categorySlug);
    if (typeof meta.note === 'string' && meta.note) pushMetaValue(out, used, 'note', 'Nota', meta.note);
    if (typeof meta.voidReason === 'string' && meta.voidReason) {
      pushMetaValue(out, used, 'voidReason', 'Motivo anulación', meta.voidReason);
    }
    if (meta.before != null) pushMetaValue(out, used, 'before', 'Antes', meta.before);
    if (meta.after != null) pushMetaValue(out, used, 'after', 'Después', meta.after);
  } else if (t.startsWith('reports.')) {
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
    if (typeof meta.sessionId === 'string') pushMetaValue(out, used, 'sessionId', 'Sesión', meta.sessionId);
    if (typeof meta.openingAmount === 'number') {
      pushMetaValue(out, used, 'openingAmount', 'Fondo inicial', formatMoney(meta.openingAmount));
    }
    if (typeof meta.closingAmount === 'number') {
      pushMetaValue(out, used, 'closingAmount', 'Monto contado', formatMoney(meta.closingAmount));
    }
    if (typeof meta.expectedAmount === 'number') {
      pushMetaValue(out, used, 'expectedAmount', 'Esperado', formatMoney(meta.expectedAmount));
    }
    if (typeof meta.difference === 'number') {
      pushMetaValue(out, used, 'difference', 'Diferencia', formatMoney(meta.difference));
    }
    if (typeof meta.sessionCashTotal === 'number') {
      pushMetaValue(out, used, 'sessionCashTotal', 'Efectivo del turno', formatMoney(meta.sessionCashTotal));
    }
    if (typeof meta.outflowsTotal === 'number') {
      pushMetaValue(out, used, 'outflowsTotal', 'Egresos del turno', formatMoney(meta.outflowsTotal));
    }
    if (typeof meta.outflowId === 'string') pushMetaValue(out, used, 'outflowId', 'Egreso', meta.outflowId);
    if (typeof meta.category === 'string') pushMetaValue(out, used, 'category', 'Categoría', meta.category);
    if (typeof meta.amount === 'number') pushMetaValue(out, used, 'amount', 'Monto', formatMoney(meta.amount));
    if (typeof meta.note === 'string' && meta.note) pushMetaValue(out, used, 'note', 'Nota', meta.note);
    if (typeof meta.voidReason === 'string' && meta.voidReason) {
      pushMetaValue(out, used, 'voidReason', 'Motivo anulación', meta.voidReason);
    }
    if (typeof meta.openedBy === 'string') pushMetaValue(out, used, 'openedBy', 'Abierto por', meta.openedBy);
    if (typeof meta.closedBy === 'string') pushMetaValue(out, used, 'closedBy', 'Cerrado por', meta.closedBy);
    if (typeof meta.createdBy === 'string') pushMetaValue(out, used, 'createdBy', 'Registrado por', meta.createdBy);
    if (typeof meta.voidedBy === 'string') pushMetaValue(out, used, 'voidedBy', 'Anulado por', meta.voidedBy);
    if (meta.before != null) pushMetaValue(out, used, 'before', 'Antes', meta.before);
    if (meta.after != null) pushMetaValue(out, used, 'after', 'Después', meta.after);
    if (typeof meta.type === 'string') pushMetaValue(out, used, 'type', 'Tipo', meta.type);
    if (typeof meta.externalStoreId === 'string') {
      pushMetaValue(out, used, 'externalStoreId', 'Tienda externa', meta.externalStoreId);
    }
  } else if (t.startsWith('branch.settings.')) {
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
  } else if (t === 'employee.revoked' || t === 'employee.branch.add' || t === 'employee.branch.remove') {
    if (typeof meta.employeeName === 'string') pushMetaValue(out, used, 'employeeName', 'Nombre', meta.employeeName);
    if (typeof meta.employeeId === 'string') pushMetaValue(out, used, 'employeeId', 'ID empleado', meta.employeeId);
    if (typeof meta.branchId === 'string') pushMetaValue(out, used, 'branchId', 'Sucursal', meta.branchId);
  } else if (t.startsWith('catalog.category.')) {
    if (typeof meta.categoryId === 'string') pushMetaValue(out, used, 'categoryId', 'Categoría', meta.categoryId);
    if (typeof meta.slug === 'string') pushMetaValue(out, used, 'slug', 'Slug', meta.slug);
  }

  for (const k of Object.keys(meta).sort()) {
    if (used.has(k)) continue;
    pushMetaValue(out, used, k, k, meta[k]);
  }

  return out;
}

export function groupAuditRowsByDay(rows: AuditLogRow[]): { title: string; data: AuditLogRow[] }[] {
  const dayKeyFromDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const now = new Date();
  const todayKey = dayKeyFromDate(now);
  const y = new Date(now);
  y.setDate(y.getDate() - 1);
  const yesterdayKey = dayKeyFromDate(y);

  const map = new Map<string, AuditLogRow[]>();
  for (const row of rows) {
    const d = new Date(row.created_at);
    const k = Number.isNaN(d.getTime()) ? 'unknown' : dayKeyFromDate(d);
    if (!map.has(k)) map.set(k, []);
    map.get(k)!.push(row);
  }

  const keys = [...map.keys()].filter((k) => k !== 'unknown').sort((a, b) => b.localeCompare(a));
  if (map.has('unknown')) keys.push('unknown');

  return keys.map((k) => {
    let title: string;
    if (k === 'unknown') title = 'Fecha desconocida';
    else if (k === todayKey) title = 'Hoy';
    else if (k === yesterdayKey) title = 'Ayer';
    else {
      const [yy, mm, dd] = k.split('-').map(Number);
      const date = new Date(yy, mm - 1, dd);
      title = date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    return { title, data: map.get(k)! };
  });
}

export type { AuditCategory } from './auditCategories';
export { AUDIT_CATEGORY_LABELS, categorizeEventType } from './auditCategories';

export function getAuditEventIconName(eventType: string): keyof typeof Ionicons.glyphMap {
  if (eventType.startsWith('login') || eventType.startsWith('register')) return 'log-in-outline';
  if (eventType.startsWith('logout')) return 'log-out-outline';
  if (eventType.startsWith('employee') || eventType.startsWith('invitation')) return 'people-outline';
  if (eventType.startsWith('catalog')) return 'pricetag-outline';
  if (eventType.startsWith('reports')) return 'bar-chart-outline';
  if (eventType.startsWith('pos')) return 'cart-outline';
  if (eventType.startsWith('stock')) return 'cube-outline';
  if (eventType.startsWith('fiado')) return 'wallet-outline';
  if (eventType.startsWith('expense')) return 'wallet-outline';
  if (eventType.startsWith('branch.settings')) return 'business-outline';
  return 'document-text-outline';
}
